import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen, Video, Podcast, Film, Bookmark, BookmarkCheck, ExternalLink, Users } from 'lucide-react';
import { toast } from 'sonner';

const mediaTypeIcons = {
  book: BookOpen,
  video: Video,
  podcast: Podcast,
  documentary: Film
};

export default function MediaDetailPage() {
  const { mediaId } = useParams();
  const { user } = useAuth();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState(null);

  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/media/${mediaId}`);
        setMedia(res.data);
      } catch (error) {
        console.error('Error fetching media:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, [mediaId]);

  // Check if bookmarked
  useEffect(() => {
    if (!user) return;
    const checkBookmark = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/bookmarks`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        const bookmark = res.data.find(b => b.item_type === 'media' && b.item_id === mediaId);
        if (bookmark) {
          setIsBookmarked(true);
          setBookmarkId(bookmark.bookmark_id);
        }
      } catch (error) {
        console.error('Error checking bookmark:', error);
      }
    };
    checkBookmark();
  }, [user, mediaId]);

  const handleBookmark = async () => {
    if (!user) {
      toast.error('Please sign in to bookmark');
      return;
    }
    
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${bookmarkId}`, { headers, withCredentials: true });
        setIsBookmarked(false);
        setBookmarkId(null);
        toast.success('Bookmark removed');
      } else {
        const res = await axios.post(`${API}/bookmarks`, 
          { item_type: 'media', item_id: mediaId },
          { headers, withCredentials: true }
        );
        setIsBookmarked(true);
        setBookmarkId(res.data.bookmark_id);
        toast.success('Bookmarked!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating bookmark');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="skeleton h-8 w-32 mb-8" />
          <div className="skeleton h-64 rounded-sm mb-8" />
          <div className="skeleton h-4 w-3/4 mb-4" />
          <div className="skeleton h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/50">Media not found</p>
      </div>
    );
  }

  const Icon = mediaTypeIcons[media.media_type] || BookOpen;

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link to="/media" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm uppercase tracking-wider">Back to Library</span>
        </Link>

        {/* Header */}
        <div className="card-dark overflow-hidden mb-8" data-testid="media-detail">
          {/* Thumbnail */}
          {media.thumbnail_url && (
            <div className="relative h-64 md:h-80">
              <img src={media.thumbnail_url} alt={media.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              
              {/* Type badge */}
              <div className="absolute bottom-6 left-6">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-mono uppercase tracking-wider bg-black/50 backdrop-blur rounded-sm">
                  <Icon className="w-4 h-4" />
                  {media.media_type}
                </span>
              </div>
            </div>
          )}

          <div className="p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold mb-3">{media.title}</h1>
                {media.authors?.length > 0 && (
                  <p className="text-cyan-400 font-mono">by {media.authors.join(', ')}</p>
                )}
              </div>
              <Button
                variant="ghost"
                onClick={handleBookmark}
                className="text-white/60 hover:text-white"
                data-testid="bookmark-btn"
              >
                {isBookmarked ? (
                  <BookmarkCheck className="w-6 h-6 text-cyan-400" />
                ) : (
                  <Bookmark className="w-6 h-6" />
                )}
              </Button>
            </div>

            <p className="text-white/70 leading-relaxed text-lg">{media.description}</p>
          </div>
        </div>

        {/* Embedded Video */}
        {media.embed_url && (
          <div className="card-dark overflow-hidden mb-8">
            <div className="aspect-video">
              <iframe
                src={media.embed_url}
                title={media.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Key Revelations */}
        {media.key_revelations?.length > 0 && (
          <div className="card-dark p-6 mb-8">
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-cyan-400" />
              Key Revelations
            </h2>
            <ul className="space-y-3">
              {media.key_revelations.map((rev, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="text-cyan-400 mt-1">•</span>
                  <span className="text-white/70">{rev}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Related People */}
        {media.related_people?.length > 0 && (
          <div className="card-dark p-6">
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Related People
            </h2>
            <div className="flex flex-wrap gap-3">
              {media.related_people.map(personId => (
                <Link
                  key={personId}
                  to={`/people/${personId}`}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-sm hover:border-cyan-400/50 transition-colors"
                >
                  {personId.replace('person_', '').replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
